import { useMemo, useRef, useState } from "preact/hooks";
import { localStorageAccess } from "../utils/web-storage.ts";

type Options<T> = {
	readonly key: string;
	readonly defaultValue: T;
	readonly parse: (raw: string) => T;
	readonly stringify: (value: T) => string | undefined;
};

function useLocalStorage<T>({
	key,
	defaultValue,
	parse,
	stringify,
}: Options<T>): Readonly<[T, (value: T) => void]> {
	const [value, setValue] = useState<T>(() => {
		// Falls back to the shared in-memory store when storage is unavailable, so
		// a value written by another component this page load is still visible.
		const rawValue = localStorageAccess.read(key);

		// Not yet stored
		if (rawValue === null) {
			return defaultValue;
		}

		// Try and parse
		try {
			return parse(rawValue);
		} catch (e) {
			console.error(`Error parsing local storage ${key} value ${rawValue}`, e);
			return defaultValue;
		}
	});

	const defaultValueRef = useRef(defaultValue);
	defaultValueRef.current = defaultValue;
	const stringifyRef = useRef(stringify);
	stringifyRef.current = stringify;

	// The stored value is read once for the initial key and never re-read, so a
	// changing key is not supported (reads would stay on the old key while writes
	// target the new one). Surface it loudly rather than silently misbehaving.
	const initialKeyRef = useRef(key);
	if (initialKeyRef.current !== key) {
		console.error(
			`useLocalStorage does not support a changing key (was "${initialKeyRef.current}", now "${key}"); the value will keep reflecting the original key.`,
		);
	}

	return useMemo(
		() => [
			value,
			(newValue: T) => {
				setValue(newValue);

				if (newValue === defaultValueRef.current) {
					localStorageAccess.write(key, undefined);
					return;
				}
				localStorageAccess.write(key, stringifyRef.current(newValue));
			},
		],
		[value, key, defaultValueRef],
	);
}

export default useLocalStorage;
